import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Star,
  MessageSquare,
  AlertCircle,
  ThumbsUp,
  Bug,
  Lightbulb,
  Clock,
  Users,
  Award,
  Shield,
  Globe,
  Search as SearchIcon,
  Loader2,
} from "lucide-react";
import moment from "moment-timezone";
import { submitContactRequest, submitFeedback } from "../apis/settings";
import { useTimezone } from "../contexts/TimezoneContext";
import { useBranding } from "../contexts/BrandingContext";

const FEEDBACK_TYPES = [
  { value: "suggestion", label: "Suggestion", icon: Lightbulb },
  { value: "complaint", label: "Complaint", icon: AlertCircle },
  { value: "compliment", label: "Compliment", icon: ThumbsUp },
  { value: "bug_report", label: "Bug report", icon: Bug },
];

const Settings = () => {
  const brand = useBranding();
  const [activeTab, setActiveTab] = useState("timezone");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [feedbackForm, setFeedbackForm] = useState({
    name: "",
    email: "",
    rating: 0,
    feedback_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const {
    timezone: accountTimezone,
    fallbackTimezone,
    canEdit: canEditTimezone,
    saving: timezoneSaving,
    availableTimezones,
    setTimezone: saveAccountTimezone,
  } = useTimezone();

  const [selectedTz, setSelectedTz] = useState(accountTimezone || fallbackTimezone);
  const [tzSearch, setTzSearch] = useState("");

  useEffect(() => {
    setSelectedTz(accountTimezone || fallbackTimezone);
  }, [accountTimezone, fallbackTimezone]);

  const filteredTimezones = useMemo(() => {
    const q = tzSearch.trim().toLowerCase();
    if (!q) return availableTimezones;
    return availableTimezones.filter(
      (t) => t.name.toLowerCase().includes(q) || t.label.toLowerCase().includes(q)
    );
  }, [availableTimezones, tzSearch]);

  const handleSaveTimezone = async () => {
    if (!canEditTimezone) return;
    if (!selectedTz) {
      toast.error("Please pick a timezone");
      return;
    }
    await saveAccountTimezone(selectedTz);
  };

  const previewNow = useMemo(() => {
    const zone =
      selectedTz && moment.tz.zone(selectedTz) ? selectedTz : fallbackTimezone;
    return moment().tz(zone).format("MMM DD, YYYY hh:mm:ss A");
  }, [selectedTz, fallbackTimezone]);

  const companyInfo = {
    phone: brand.company_phone || "+91 97277 89826",
    email: brand.supportEmail || brand.support_email || brand.company_email || (brand.id ? "" : "sales@blutec.ai"),
    address: brand.company_address || "Ahmedabad, Gujarat India",
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await submitContactRequest(contactForm);
      if (response.data.success) {
        toast.success("Contact request submitted successfully! We'll get back to you soon.");
        setContactForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        toast.error(response.data.message || "Failed to submit contact request");
      }
    } catch (error) {
      console.error("Contact submission error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await submitFeedback(feedbackForm);
      if (response.data.success) {
        toast.success("Thank you for your feedback! Your input helps us improve.");
        setFeedbackForm({ name: "", email: "", rating: 0, feedback_type: "", message: "" });
      } else {
        toast.error(response.data.message || "Failed to submit feedback");
      }
    } catch (error) {
      console.error("Feedback submission error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const dirtyTz = selectedTz !== (accountTimezone || fallbackTimezone);

  return (
    <div className="set-page">
      <div className="page-head">
        <div>
          <h2>Settings</h2>
          <p>Account preferences and support</p>
        </div>
      </div>

      <div className="fl-kpis">
        <div className="fl-kpi hi">
          <div>
            <div className="k">Happy customers</div>
            <div className="v">10,000+</div>
          </div>
          <div className="ico"><Users size={14} /></div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Response time</div>
            <div className="v">&lt; 2 hrs</div>
          </div>
          <div className="ico"><Clock size={14} /></div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Satisfaction</div>
            <div className="v">98%</div>
          </div>
          <div className="ico"><Award size={14} /></div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Data security</div>
            <div className="v">100%</div>
          </div>
          <div className="ico"><Shield size={14} /></div>
        </div>
      </div>

      <div className="ping-pill-tabs">
        <button
          type="button"
          className={`ping-ptab ${activeTab === "timezone" ? "on" : ""}`}
          onClick={() => setActiveTab("timezone")}
        >
          <Globe size={14} />
          Timezone
        </button>
        <button
          type="button"
          className={`ping-ptab ${activeTab === "contact" ? "on" : ""}`}
          onClick={() => setActiveTab("contact")}
        >
          <Phone size={14} />
          Contact us
        </button>
        <button
          type="button"
          className={`ping-ptab ${activeTab === "feedback" ? "on" : ""}`}
          onClick={() => setActiveTab("feedback")}
        >
          <MessageSquare size={14} />
          Feedback
        </button>
      </div>

      {activeTab === "timezone" && (
        <div className="set-grid">
          <section className="ping-card">
            <h3>Account timezone</h3>
            <div className="sub">
              Used for conversation timestamps and “time ago” labels. Stored in UTC, converted on display. Shared across the account.
            </div>

            {!canEditTimezone && (
              <div className="ping-banner amber" style={{ marginTop: 12 }}>
                <AlertCircle size={16} />
                <div>Only the account owner can change the timezone.</div>
              </div>
            )}

            <div className="ping-kv">
              <span className="k">Current timezone</span>
              <span className="v">{accountTimezone || `${fallbackTimezone} (browser default)`}</span>
            </div>
            <div className="ping-kv">
              <span className="k">Preview now</span>
              <span className="v">{previewNow}</span>
            </div>

            <div className="set-tz-search">
              <SearchIcon size={14} />
              <input
                type="text"
                placeholder="Search timezone (Kolkata, New_York, UTC+05)"
                value={tzSearch}
                onChange={(e) => setTzSearch(e.target.value)}
                disabled={!canEditTimezone}
              />
            </div>

            <label className="set-label">Pick a timezone</label>
            <select
              className="set-tz-list"
              value={selectedTz || ""}
              onChange={(e) => setSelectedTz(e.target.value)}
              disabled={!canEditTimezone}
              size={8}
            >
              {filteredTimezones.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.label}
                </option>
              ))}
              {filteredTimezones.length === 0 && (
                <option disabled>No matching timezones</option>
              )}
            </select>

            <div className="set-foot">
              <span className="set-meta">
                {filteredTimezones.length} of {availableTimezones.length} timezones
              </span>
              <button
                type="button"
                className="ping-btn ping-btn-green ping-btn-sm disabled:opacity-50"
                onClick={handleSaveTimezone}
                disabled={!canEditTimezone || timezoneSaving || !dirtyTz}
              >
                {timezoneSaving ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />}
                {timezoneSaving ? "Saving…" : "Save timezone"}
              </button>
            </div>
          </section>

          <section className="ping-card">
            <h3>{brand.company_name || brand.name}</h3>
            <div className="sub">We typically reply within 2 hours on business days</div>
            <div className="ping-kv">
              <span className="k">Phone</span>
              <span className="v">{companyInfo.phone}</span>
            </div>
            <div className="ping-kv">
              <span className="k">Email</span>
              <span className="v">{companyInfo.email}</span>
            </div>
            <div className="ping-kv">
              <span className="k">Address</span>
              <span className="v">{companyInfo.address}</span>
            </div>
          </section>
        </div>
      )}

      {activeTab === "contact" && (
        <div className="set-grid set-grid-form">
          <section className="ping-card">
            <h3>Contact form</h3>
            <div className="sub">We’ll get back to you on the email you share</div>
            <form className="set-form" onSubmit={handleContactSubmit}>
              <div className="set-row">
                <label>
                  Your name *
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                  />
                </label>
                <label>
                  Your email *
                  <input
                    type="email"
                    placeholder="john@example.com"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                  />
                </label>
              </div>
              <div className="set-row">
                <label>
                  Phone
                  <input
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  />
                </label>
                <label>
                  Subject *
                  <input
                    type="text"
                    placeholder="How can we help?"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    required
                  />
                </label>
              </div>
              <label>
                Message *
                <textarea
                  rows={4}
                  placeholder="Tell us more about your inquiry…"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                />
              </label>
              <div className="set-foot">
                <span />
                <button type="submit" className="ping-btn ping-btn-green ping-btn-sm disabled:opacity-50" disabled={loading}>
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {loading ? "Sending…" : "Send message"}
                </button>
              </div>
            </form>
          </section>
          <section className="ping-card">
            <h3>Get in touch</h3>
            <div className="sub">Reach the BluTec team directly</div>
            <div className="ping-kv">
              <span className="k"><Phone size={13} /> Phone</span>
              <span className="v">{companyInfo.phone}</span>
            </div>
            <div className="ping-kv">
              <span className="k"><Mail size={13} /> Email</span>
              <span className="v">{companyInfo.email}</span>
            </div>
            <div className="ping-kv">
              <span className="k"><MapPin size={13} /> Address</span>
              <span className="v">{companyInfo.address}</span>
            </div>
          </section>
        </div>
      )}

      {activeTab === "feedback" && (
        <section className="ping-card">
          <h3>Share your feedback</h3>
          <div className="sub">Ratings and notes help us improve the panel</div>
          <form className="set-form" onSubmit={handleFeedbackSubmit}>
            <div className="set-row">
              <label>
                Your name *
                <input
                  type="text"
                  placeholder="John Doe"
                  value={feedbackForm.name}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
                  required
                />
              </label>
              <label>
                Your email *
                <input
                  type="email"
                  placeholder="john@example.com"
                  value={feedbackForm.email}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, email: e.target.value })}
                  required
                />
              </label>
            </div>

            <div>
              <span className="set-label">Rate your experience</span>
              <div className="set-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={star <= feedbackForm.rating ? "on" : ""}
                    onClick={() => setFeedbackForm({ ...feedbackForm, rating: star })}
                    aria-label={`${star} star${star > 1 ? "s" : ""}`}
                  >
                    <Star size={18} fill={star <= feedbackForm.rating ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="set-label">Feedback type *</span>
              <div className="set-types">
                {FEEDBACK_TYPES.map((type) => {
                  const Icon = type.icon;
                  const on = feedbackForm.feedback_type === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      className={on ? "on" : ""}
                      onClick={() => setFeedbackForm({ ...feedbackForm, feedback_type: type.value })}
                    >
                      <Icon size={14} />
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <label>
              Your feedback *
              <textarea
                rows={4}
                placeholder="Share your thoughts with us…"
                value={feedbackForm.message}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                required
              />
            </label>
            <div className="set-foot">
              <span />
              <button type="submit" className="ping-btn ping-btn-green ping-btn-sm disabled:opacity-50" disabled={loading}>
                {loading ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                {loading ? "Submitting…" : "Submit feedback"}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
};

export default Settings;
